import { EnsureTree } from "event-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { raiseOnce } from "library/local-events/index.mjs"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { lazy, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import { useScheduleWithStatus } from "routes/schedule/lib/useSchedule"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { LoadingTemplate } from "lib/@components/loading-template"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { getScheduleRecordId } from "library/get-unique-id-from-tree.mjs"
import { getTreeId } from "library/id-helpers.mjs"
import { parentTreeItem } from "library/tree/index.mjs"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { TranslationStateProvider } from "routes/schedule/plugins/translation/translation-state-provider"
const ScheduleDisplay = lazy(() => import("./scheduleDisplay"))

const EMPTY_TRANSLATION_STATE = {
    loading: false,
    isTranslated: false,
    language: "en",
    rtl: false,
    error: null,
    translationMeta: null,
    labels: null,
    translatedSkills: null,
}

export default function Schedule({ scheduleId, version }) {
    const { dataProvider = useScheduleWithStatus, draft } = useBoundContext()
    const { id: paramId } = useParams()
    const id = scheduleId ?? paramId
    const [urlVersion] = useSearchParam("version", "no_v")

    version ??= urlVersion
    if (draft) version = draft
    const code = getScheduleRecordId(id)
    const [editing] = useSearchParam("editEx")
    const treeId = getTreeId(id)
    const treeItem = useTreeItem(id)
    const { schedule: rawSchedule, ...props } = dataProvider(!treeId || editing ? code : id, version)
    const translationState = useRef(EMPTY_TRANSLATION_STATE)

    if (treeId) {
        EnsureTree.raise(treeId)
    }

    useEffect(() => {
        raiseOnce("CurrentSchedule", id)
    }, [id])

    const tree = useTreeItem(treeId)

    return !tree && treeId ? (
        <LoadingScreen description="Has Tree" />
    ) : (
        <Bound
            schedulePath={id}
            code={code}
            parentItem={treeItem?.[parentTreeItem]}
            scheduleItem={treeItem}
            translationState={translationState}
        >
            <LoadingTemplate
                {...props}
                errorHeader="Can't display this schedule"
                target={rawSchedule}
                schedule={rawSchedule}
            >
                <TranslationStateProvider>
                    <ScheduleDisplay />
                </TranslationStateProvider>
            </LoadingTemplate>
        </Bound>
    )
}
